import { useEffect, useRef, useState, type Ref } from 'react';

/**
 * Largeur mesuree d'un element, en pixels entiers.
 *
 * Les graphiques sont dessines **a la taille reelle** et non mis a l'echelle :
 * un `viewBox` reduit a 318 px sur un telephone ecraserait des etiquettes de
 * 12 px a 6 px. `fallback` vaut tant que rien n'est mesure — au premier rendu,
 * et partout ou `ResizeObserver` n'existe pas (jsdom).
 */
export const useMeasuredWidth = (fallback: number): { ref: Ref<HTMLElement>; width: number } => {
  const ref = useRef<HTMLElement | null>(null);
  const [width, setWidth] = useState(fallback);

  useEffect(() => {
    const element = ref.current;

    if (element === null || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      const measured = Math.floor(entries[0]?.contentRect.width ?? 0);

      // Un element masque (onglet ferme) mesure 0 : on garde la derniere largeur.
      if (measured > 0) {
        setWidth(measured);
      }
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  return { ref, width };
};
